import { RewritableContentType } from '@fullstory/playback-service-worker';
import { createCSSRewriter, SvgRewriter } from '@fullstory/rewriters';
import { assertExhaustive } from '@fullstory/typeutils';
import { UrlRewriter } from '@fullstory/url-rewriter';

function rewriteStyleSheet(requestUrl: string, cssText: string): Blob {
  const urlRewriter = new UrlRewriter(requestUrl);
  const cssRewriter = createCSSRewriter(urlRewriter);
  const rewrittenCss = cssRewriter.rewriteStyleSheetText(cssText).value;
  return new Blob([rewrittenCss], { type: 'text/css' });
}

function rewriteSvg(requestUrl: string, text: string): Blob {
  const urlRewriter = new UrlRewriter(requestUrl);
  const cssRewriter = createCSSRewriter(urlRewriter);
  const svgRewriter = new SvgRewriter(cssRewriter, urlRewriter);
  const rewrittenSvg = svgRewriter.rewriteSvgDocumentText(text, requestUrl);
  return new Blob([rewrittenSvg], { type: 'image/svg+xml' });
}

// Rewrites content (e.g. a stylesheet) and returns the rewritten version as a
// blob. This is used to handle rewrite requests from the service worker. If the
// content type is not supported, the original content is returned unchanged.
export async function rewriteContent(
  requestUrl: string,
  contentType: RewritableContentType | string,
  content: string,
): Promise<Blob> {
  const knownContentType = contentType as RewritableContentType;
  switch (knownContentType) {
    case 'text/css':
      console.debug(`Rewriting stylesheet: ${requestUrl}`);
      return rewriteStyleSheet(requestUrl, content);

    case 'image/svg+xml':
      console.debug(`Rewriting SVG document: ${requestUrl}`);
      return rewriteSvg(requestUrl, content);

    default:
      try {
        // Ensure that we've handled all RewritableContentType cases; this won't
        // compile if we haven't. But...
        return assertExhaustive(
          knownContentType,
          `Can't rewrite unknown content type: ${knownContentType}`,
        );
      } catch (e) {
        // ...at runtime, we may still take this code path, because this message
        // may have come from a service worker based on a newer version of the
        // script that supports rewriting more content types. We maintain
        // compatibility with older clients by having them send back the content
        // unchanged, just as it would've been before we added rewriting support
        // for that content type.
        console.warn(`Can't rewrite '${contentType}'; returning original content`);
        return new Blob([content]);
      }
  }
}
